import { useQuery } from '@tanstack/react-query';

import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

export interface IFirmwareRelease {
  firmware_version: string;
  released_at: string;
}

interface IGetOtaFirmwareReleasesResponse {
  releases: IFirmwareRelease[];
}

export const fetchOtaFirmwareReleases = async ({
  makeRequest,
}: {
  makeRequest: ReturnType<typeof useImperativeRequestWrapper>[1];
}): Promise<IGetOtaFirmwareReleasesResponse> => {
  const { data, error } = await makeRequest({
    path: '/ota/firmwares',
    method: 'get',
  });

  if (!data || error) {
    throw new Error(error?.response?.data?.message ?? error);
  }

  return data;
};

type QueryFnType = typeof fetchOtaFirmwareReleases;

type GetOtaFirmwareReleasesOptions = {
  config?: QueryConfig<QueryFnType>;
};

// Reason: Bulletproof React convention — query hooks are named getXxx (no use prefix).
// eslint-disable-next-line react-hooks/rules-of-hooks
export const getOtaFirmwareReleases = ({
  config,
}: GetOtaFirmwareReleasesOptions = {}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [, makeRequest] = useImperativeRequestWrapper('deviceV3');

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: ['ota', 'firmwares'],
    queryFn: () => fetchOtaFirmwareReleases({ makeRequest }),
  });
};
